import {
  DATE_RANGE_PRESETS,
  DEFAULT_PAGE_SIZE,
  SORT_KEYS,
  VAULT_DENSITIES,
  VAULT_VIEW_MODES,
  initialFilterState,
} from '@utils/documentVault';

const PARAM_KEYS = {
  search: 'q',
  tickers: 'tickers',
  docTypes: 'types',
  tags: 'tags',
  statuses: 'status',
  fileFormats: 'format',
  cleanEligibleOnly: 'clean',
  preset: 'period',
  from: 'from',
  to: 'to',
  sortKey: 'sort',
  sortDirection: 'dir',
  page: 'page',
  view: 'view',
  density: 'density',
};

function splitList(value) {
  if (!value) return [];
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

export function serializeFilters(filters) {
  const params = new URLSearchParams();

  // Fixed key order, defaults omitted: the same view always produces the same URL, so links
  // to it are comparable and dedupe cleanly.
  if (filters.search) params.set(PARAM_KEYS.search, filters.search);
  if (filters.tickers?.length) params.set(PARAM_KEYS.tickers, filters.tickers.join(','));
  if (filters.docTypes?.length) params.set(PARAM_KEYS.docTypes, filters.docTypes.join(','));
  if (filters.tags?.length) params.set(PARAM_KEYS.tags, filters.tags.join(','));
  if (filters.statuses?.length) params.set(PARAM_KEYS.statuses, filters.statuses.join(','));
  if (filters.fileFormats?.length) params.set(PARAM_KEYS.fileFormats, filters.fileFormats.join(','));
  if (filters.cleanEligibleOnly) params.set(PARAM_KEYS.cleanEligibleOnly, '1');

  if (filters.dateRange?.preset && filters.dateRange.preset !== DATE_RANGE_PRESETS.all) {
    params.set(PARAM_KEYS.preset, filters.dateRange.preset);
    if (filters.dateRange.from) params.set(PARAM_KEYS.from, filters.dateRange.from);
    if (filters.dateRange.to) params.set(PARAM_KEYS.to, filters.dateRange.to);
  }

  const isDefaultSort =
    filters.sort?.key === initialFilterState.sort.key && filters.sort?.direction === initialFilterState.sort.direction;
  if (!isDefaultSort) {
    params.set(PARAM_KEYS.sortKey, filters.sort.key);
    params.set(PARAM_KEYS.sortDirection, filters.sort.direction);
  }

  if (filters.page > 1) params.set(PARAM_KEYS.page, String(filters.page));
  if (filters.viewMode !== initialFilterState.viewMode) params.set(PARAM_KEYS.view, filters.viewMode);
  if (filters.density !== initialFilterState.density) params.set(PARAM_KEYS.density, filters.density);

  return params.toString();
}

export function parseFilters(search) {
  const params = new URLSearchParams(search || '');

  const preset = params.get(PARAM_KEYS.preset);
  const sortKey = params.get(PARAM_KEYS.sortKey);
  const sortDirection = params.get(PARAM_KEYS.sortDirection);
  const view = params.get(PARAM_KEYS.view);
  const density = params.get(PARAM_KEYS.density);
  const page = Number.parseInt(params.get(PARAM_KEYS.page), 10);

  return {
    search: params.get(PARAM_KEYS.search) || '',
    tickers: splitList(params.get(PARAM_KEYS.tickers)),
    docTypes: splitList(params.get(PARAM_KEYS.docTypes)),
    tags: splitList(params.get(PARAM_KEYS.tags)),
    statuses: splitList(params.get(PARAM_KEYS.statuses)),
    fileFormats: splitList(params.get(PARAM_KEYS.fileFormats)),
    cleanEligibleOnly: params.get(PARAM_KEYS.cleanEligibleOnly) === '1',
    dateRange: {
      // An unrecognized preset falls back to "any time" rather than filtering everything away.
      preset: preset && DATE_RANGE_PRESETS[preset] ? preset : DATE_RANGE_PRESETS.all,
      from: params.get(PARAM_KEYS.from) || null,
      to: params.get(PARAM_KEYS.to) || null,
    },
    sort: {
      key: sortKey && SORT_KEYS[sortKey] ? sortKey : initialFilterState.sort.key,
      direction: sortDirection === 'asc' ? 'asc' : initialFilterState.sort.direction,
    },
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: DEFAULT_PAGE_SIZE,
    viewMode: view && VAULT_VIEW_MODES[view] ? view : initialFilterState.viewMode,
    density: density && VAULT_DENSITIES[density] ? density : initialFilterState.density,
  };
}
